import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppDataProvider } from "./context/AppDataContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "sonner";
import PublicHome from "./pages/PublicHome";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Admin from "./pages/Admin";
import Portal from "./pages/Portal";
import ClinicianDashboard from "./components/ClinicianDashboard";
import { TestRoleBanner } from "./components/AdminTestRoles";
import { HealthAssistant } from "./components/HealthAssistant";
import { Analytics } from "./components/Analytics";

function StaffHome() {
  const { user } = useAuth();
  return user?.role === "clinical_specialist" ? <ClinicianDashboard /> : <Admin />;
}

function ProtectedRoute({ children, staffOnly }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-clinical font-heading text-xl">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (staffOnly && (user.role === "public" || user.role === "language_interpreter")) return <Navigate to="/portal" replace />;
  if (!staffOnly && user.role !== "public" && user.role !== "language_interpreter") return <Navigate to="/admin" replace />;
  return children;
}

function ConsultRoute() {
  const { user, loading } = useAuth();
  const { aid } = useParams();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-clinical font-heading text-xl">
        Loading…
      </div>
    );
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(`/consult/${aid}`)}`} replace />;
  if (user.role !== "public" && user.role !== "language_interpreter") return <Navigate to="/admin" replace />;
  return <Portal consultId={aid} />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute staffOnly>
            <StaffHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <Portal />
          </ProtectedRoute>
        }
      />
      <Route path="/consult/:aid" element={<ConsultRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppDataProvider>
          <LanguageProvider>
            <BrowserRouter>
              <Analytics />
              <Toaster position="bottom-right" richColors />
              <AppRouter />
              <TestRoleBanner />
              <HealthAssistant />
            </BrowserRouter>
          </LanguageProvider>
        </AppDataProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
